import * as React from 'react'

import { i18n } from '@thg-commerce/enterprise-core'
import { DynamicForm } from '@thg-commerce/gravity-patterns'
import { FormItem } from '@thg-commerce/gravity-patterns/Form'
import { ToggleButton, RadioInput, Input } from '@thg-commerce/gravity-elements'

import { getCalculatedMacroResult, convertImperialToMetric } from '../utils'
import { Macros, Gender } from '../types'
import {
  FormContainer,
  Title,
  SubHeading,
  ButtonContainer,
  Container,
  StyledGrid,
  StyledGridItem,
  StyledErrorMessage,
  StyledSeperator,
  StyledRadioGroup,
  StyledButton,
  TextContainer,
} from './styles'

export interface MacroInputFormProps {
  setMacros: (value: Macros) => void
  setShowResultsView: (value: boolean) => void
}

export const MacroInputForm = (props: MacroInputFormProps) => {
  const i18nText = {
    formTitle: i18n('widget.macrocalculator.title'),
    gender: i18n('widget.macrocalculator.gender'),
    femaleGender: i18n('widget.macrocalculator.gender.female'),
    maleGender: i18n('widget.macrocalculator.gender.male'),
    units: i18n('widget.macrocalculator.units'),
    metricUnits: i18n('widget.macrocalculator.units.metric'),
    imperialUnits: i18n('widget.macrocalculator.units.imperial'),
    age: i18n('widget.macrocalculator.age'),
    height: i18n('widget.macrocalculator.height'),
    weight: i18n('widget.macrocalculator.weight'),
    goal: i18n('widget.macrocalculator.goal'),
    loss: i18n('widget.macrocalculator.goal.loss'),
    maintain: i18n('widget.macrocalculator.goal.maintain'),
    gain: i18n('widget.macrocalculator.goal.gain'),
    activityLevel: i18n('widget.macrocalculator.activitylevel'),
    sedentary: i18n('widget.macrocalculator.activitylevel.sedentary'),
    moderatelyActive: i18n('widget.macrocalculator.activitylevel.active'),
    vigorourslyActive: i18n(
      'widget.macrocalculator.activitylevel.vigorouslyactive',
    ),
    extremelyActive: i18n(
      'widget.macrocalculator.activitylevel.extremelyactive',
    ),
    calculate: i18n('widget.macrocalculator.calculate'),
    macroInformation: i18n('widget.macrocalculator.macro.information'),
    ageRequired: i18n('widget.macrocalculator.form.validation.age.required'),
    genderRequired: i18n(
      'widget.macrocalculator.form.validation.gender.required',
    ),
    unitRequired: i18n('widget.macrocalculator.form.validation.unit.required'),
    heightRequired: i18n(
      'widget.macrocalculator.form.validation.height.required',
    ),
    weightRequired: i18n(
      'widget.macrocalculator.form.validation.weight.required',
    ),
    goalRequired: i18n('widget.macrocalculator.form.validation.goal.required'),
    activityLevelRequired: i18n(
      'widget.macrocalculator.form.validation.activitylevel.required',
    ),
  }

  const [genderSelected, setGenderSelected] = React.useState<Gender>(
    Gender.None,
  )
  const [unitSelected, setUnitSelected] = React.useState('none')
  const [calculateButtonClicked, setCalculateButtonClicked] = React.useState(
    false,
  )

  const genderValidationError: boolean =
    genderSelected === 'none' && calculateButtonClicked ? true : false

  const getRequiredText = (requiredText: string) => {
    switch (requiredText) {
      case 'gender':
        return i18nText.genderRequired || 'Please select a gender'
      case 'unit':
        return i18nText.unitRequired || 'Please select a unit'
      case 'age':
        return i18nText.ageRequired || 'Please enter your age'
      case 'height':
        return i18nText.heightRequired || 'Please enter your height'
      case 'weight':
        return i18nText.weightRequired || 'Please enter your weight'
      case 'goal':
        return i18nText.goalRequired || 'Please select a goal'
      case 'activity':
        return (
          i18nText.activityLevelRequired || 'Please select an activity level'
        )
      default:
        return ''
    }
  }

  const imperialHeightFields = [
    {
      fieldName: 'heightInFeet',
      renderField: (ref: React.RefObject<HTMLInputElement>, key: string) => (
        <React.Fragment>
          <SubHeading>{i18nText.height || 'Height'}</SubHeading>
          <StyledGrid>
            <StyledGridItem colSpan={[12, 12, 6, 6]} colStart={1}>
              <FormItem bindref={ref} offset={0} key={key}>
                <Input
                  placeholder="ft"
                  label={getRequiredText('height')}
                  labelHidden={true}
                  type="number"
                  minValue="0"
                  required={true}
                  i18nText={{
                    requiredError: getRequiredText('height'),
                  }}
                />
              </FormItem>
            </StyledGridItem>
          </StyledGrid>
        </React.Fragment>
      ),
    },
    {
      fieldName: 'heightInInches',
      renderField: (ref: React.RefObject<HTMLInputElement>, key: string) => (
        <StyledGrid>
          <StyledGridItem colSpan={[12, 12, 6, 6]} colStart={1}>
            <FormItem bindref={ref} offset={0} key={key}>
              <Input
                placeholder="in"
                label={getRequiredText('height')}
                labelHidden={true}
                type="number"
                minValue="0"
                required={true}
                i18nText={{
                  requiredError: getRequiredText('height'),
                }}
              />
            </FormItem>
          </StyledGridItem>
        </StyledGrid>
      ),
    },
  ]

  const metricHeightFields = [
    {
      fieldName: 'heightInCm',
      renderField: (ref: React.RefObject<HTMLInputElement>, key: string) => (
        <React.Fragment>
          <SubHeading>{i18nText.height || 'Height'}</SubHeading>
          <FormItem bindref={ref} offset={0} key={key}>
            <Input
              placeholder="cm"
              label={getRequiredText('height')}
              labelHidden={true}
              type="number"
              minValue="0"
              required={true}
              i18nText={{
                requiredError: getRequiredText('height'),
              }}
            />
          </FormItem>
        </React.Fragment>
      ),
    },
  ]

  const imperialWeightFields = [
    {
      fieldName: 'weightInStone',
      renderField: (ref: React.RefObject<HTMLInputElement>, key: string) => (
        <React.Fragment>
          <SubHeading>{i18nText.weight || 'Weight'}</SubHeading>
          <StyledGrid>
            <StyledGridItem colSpan={[12, 12, 6, 6]} colStart={1}>
              <FormItem bindref={ref} offset={0} key={key}>
                <Input
                  placeholder="st"
                  label={getRequiredText('weight')}
                  labelHidden={true}
                  type="number"
                  minValue="0"
                  required={true}
                  i18nText={{
                    requiredError: getRequiredText('weight'),
                  }}
                />
              </FormItem>
            </StyledGridItem>
          </StyledGrid>
        </React.Fragment>
      ),
    },
    {
      fieldName: 'weightInPounds',
      renderField: (ref: React.RefObject<HTMLInputElement>, key: string) => (
        <React.Fragment>
          <StyledGrid>
            <StyledGridItem colSpan={[12, 12, 6, 6]} colStart={1}>
              <FormItem bindref={ref} offset={0} key={key}>
                <Input
                  placeholder="lb"
                  label={getRequiredText('weight')}
                  labelHidden={true}
                  type="number"
                  minValue="0"
                  required={true}
                  i18nText={{
                    requiredError: getRequiredText('weight'),
                  }}
                />
              </FormItem>
            </StyledGridItem>
          </StyledGrid>
          <StyledSeperator />
        </React.Fragment>
      ),
    },
  ]

  const metricWeightFields = [
    {
      fieldName: 'weightInKg',
      renderField: (ref: React.RefObject<HTMLInputElement>, key: string) => (
        <React.Fragment>
          <SubHeading>{i18nText.weight || 'Weight'}</SubHeading>{' '}
          <FormItem bindref={ref} offset={0} key={key}>
            <Input
              placeholder="kg"
              label={getRequiredText('weight')}
              labelHidden={true}
              type="number"
              minValue="0"
              required={true}
              i18nText={{
                requiredError: getRequiredText('weight'),
              }}
            />
          </FormItem>
          <StyledSeperator />
        </React.Fragment>
      ),
    },
  ]

  const onSubmitHandler = () => {
    setCalculateButtonClicked(true)
    return null
  }

  return (
    <React.Fragment>
      <FormContainer data-testid="macro-input-container">
        <Title> {i18nText.formTitle || 'Macronutrient Calculator'} </Title>
        <SubHeading> {i18nText.gender || 'Gender'} </SubHeading>
        <Container>
          <ButtonContainer aria-label={getRequiredText('gender')}>
            <ToggleButton
              selected={genderSelected === 'female'}
              onClick={() => {
                setGenderSelected(Gender.Female)
              }}
              error={genderValidationError}
            >
              {i18nText.femaleGender || 'Female'}
            </ToggleButton>
            <ToggleButton
              selected={genderSelected === 'male'}
              onClick={() => {
                setGenderSelected(Gender.Male)
              }}
              error={genderValidationError}
            >
              {i18nText.maleGender || 'Male'}
            </ToggleButton>
          </ButtonContainer>
          {genderValidationError && (
            <StyledErrorMessage
              id={i18nText.genderRequired}
              error={i18nText.genderRequired || 'Please select a gender'}
            />
          )}
        </Container>
        <SubHeading> {i18nText.units || 'Units'} </SubHeading>
        <Container>
          <ButtonContainer aria-label={getRequiredText('unit')}>
            <ToggleButton
              selected={unitSelected === 'metric'}
              onClick={() => {
                setUnitSelected('metric')
              }}
            >
              {i18nText.metricUnits || 'Metric'}
            </ToggleButton>
            <ToggleButton
              selected={unitSelected === 'imperial'}
              onClick={() => {
                setUnitSelected('imperial')
              }}
            >
              {i18nText.imperialUnits || 'Imperial'}
            </ToggleButton>
          </ButtonContainer>
        </Container>
        <DynamicForm
          onSubmit={({
            age,
            heightInCm,
            heightInFeet,
            heightInInches,
            weightInKg,
            weightInStone,
            weightInPounds,
            selectedGoal,
            activity,
          }) => {
            const metricMeasurement = convertImperialToMetric({
              feet: heightInFeet,
              inches: heightInInches,
              stone: weightInStone,
              pounds: weightInPounds,
            })

            if (genderSelected !== 'none') {
              const calculatedMacros = getCalculatedMacroResult({
                age,
                activity,
                selectedGoal,
                selectedGender: genderSelected,
                weightInKg:
                  unitSelected === 'imperial'
                    ? metricMeasurement.weightInKg
                    : weightInKg,
                heightInCM:
                  unitSelected === 'imperial'
                    ? metricMeasurement.heightInCm
                    : heightInCm,
              })

              props.setMacros(calculatedMacros)
              props.setShowResultsView(true)
              return null
            }
            return null
          }}
          fields={[
            {
              fieldName: 'age',
              renderField: (
                ref: React.RefObject<HTMLInputElement>,
                key: string,
              ) => (
                <React.Fragment>
                  <SubHeading> {i18nText.age || 'Age'} </SubHeading>
                  <FormItem bindref={ref} offset={0} key={key}>
                    <Input
                      label={getRequiredText('age')}
                      labelHidden={true}
                      type="number"
                      minValue="0"
                      required={true}
                      i18nText={{
                        requiredError: getRequiredText('age'),
                      }}
                    />
                  </FormItem>
                </React.Fragment>
              ),
            },
            unitSelected === 'imperial'
              ? imperialHeightFields[0]
              : metricHeightFields[0],
            unitSelected === 'imperial'
              ? imperialHeightFields[1]
              : {
                  fieldName: 'emptyHeightField',
                  renderField: () => <React.Fragment />,
                },
            unitSelected === 'imperial'
              ? imperialWeightFields[0]
              : metricWeightFields[0],
            unitSelected === 'imperial'
              ? imperialWeightFields[1]
              : {
                  fieldName: 'emptyWeightField',
                  renderField: () => <React.Fragment />,
                },
            {
              fieldName: 'selectedGoal',
              renderField: (
                ref: React.RefObject<HTMLInputElement>,
                key: string,
              ) => (
                <React.Fragment>
                  <SubHeading> {i18nText.goal || 'Goal'} </SubHeading>
                  <StyledRadioGroup
                    columns={1}
                    label={getRequiredText('goal')}
                    labelHidden={true}
                    required={true}
                    bindref={ref}
                    key={key}
                    i18nText={{
                      requiredError: getRequiredText('goal'),
                    }}
                  >
                    <RadioInput
                      label={i18nText.loss || 'Fat/Weight loss'}
                      value="lose"
                      name={i18nText.goal || 'Goal'}
                    />
                    <RadioInput
                      label={i18nText.maintain || 'Maintain muscle/weight'}
                      value="maintain"
                      name={i18nText.goal || 'Goal'}
                    />
                    <RadioInput
                      label={i18nText.gain || 'Muscle/weight gain'}
                      value="gain"
                      name={i18nText.goal || 'Goal'}
                    />
                  </StyledRadioGroup>
                </React.Fragment>
              ),
            },
            {
              fieldName: 'activity',
              renderField: (
                ref: React.RefObject<HTMLInputElement>,
                key: string,
              ) => (
                <React.Fragment>
                  <SubHeading>
                    {i18nText.activityLevel || 'Activity level'}
                  </SubHeading>
                  <StyledRadioGroup
                    columns={1}
                    label={getRequiredText('activity')}
                    labelHidden={true}
                    required={true}
                    bindref={ref}
                    key={key}
                    i18nText={{
                      requiredError: getRequiredText('activity'),
                    }}
                  >
                    <RadioInput
                      label={i18nText.sedentary || 'Sedentary'}
                      value="sedentary"
                      name={i18nText.activityLevel || 'Activity level'}
                    />
                    <RadioInput
                      label={i18nText.moderatelyActive || 'Moderately active'}
                      value="moderate"
                      name={i18nText.activityLevel || 'Activity level'}
                    />
                    <RadioInput
                      label={i18nText.vigorourslyActive || 'Vigorously active'}
                      value="vigorous"
                      name={i18nText.activityLevel || 'Activity level'}
                    />
                    <RadioInput
                      label={i18nText.extremelyActive || 'Extremely active'}
                      value="extreme"
                      name={i18nText.activityLevel || 'Activity level'}
                    />
                  </StyledRadioGroup>
                </React.Fragment>
              ),
            },
          ]}
        >
          <StyledButton
            emphasis="high"
            data-testid="calculate-button"
            sizing="regular"
            type="submit"
            onClick={onSubmitHandler}
          >
            {i18nText.calculate || 'Calculate'}
          </StyledButton>
        </DynamicForm>
        <TextContainer>
          {i18nText.macroInformation ||
            'Protein and fat values based on 2018 ISSN exercise & sports nutrition review. Carbohydrate values represent remaining calories after protein and fat are calculated.'}
        </TextContainer>
      </FormContainer>
    </React.Fragment>
  )
}
